import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../lib/data";

import { Seo } from "../components/site/Seo";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={IMAGES.hero} alt="OneHarvest" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <div className="flex items-center gap-3">
            <img src={IMAGES.markWhite} alt="OneHarvest" className="h-11 w-11 object-contain" />
            <div className="font-display font-extrabold text-2xl text-white">ONEHARVEST</div>
          </div>
          <p className="mt-4 text-white/70 max-w-sm">Admin console — manage quote requests and contact submissions.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm">
          <div className="h-12 w-12 bg-ink flex items-center justify-center">
            <Lock className="h-5 w-5 text-flame" />
          </div>
          <h1 className="mt-6 font-display font-extrabold text-3xl text-ink">Admin Sign In</h1>
          <p className="mt-2 text-muted-foreground text-sm">Restricted access.</p>

          {error && <div data-testid="login-error" className="mt-6 border border-destructive/40 bg-destructive/10 text-destructive text-sm px-4 py-3">{error}</div>}

          <div className="mt-6 space-y-4">
            <div>
              <label className="kicker text-charcoal block mb-2">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame" />
            </div>
            <div>
              <label className="kicker text-charcoal block mb-2">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-input bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-flame" />
            </div>
          </div>

          <button data-testid="login-submit" disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
